import React, { useState } from "react";
import { container_classname } from "./constants";

export interface EventTimelineItem {
    time: string;
    category: string;
    title: string;
    description: string;
    tags: { label: string; type: string }[];
    dot: "outline" | "filled";
}

export interface EventTimelineProps {
    title?: string;
    subtitle?: string;
    items?: EventTimelineItem[];
}

const badgeColorMap = {
    yellow: "bg-yellow-100 text-yellow-800",
    purple: "bg-purple-100 text-purple-800",
    green: "bg-green-100 text-green-700",
    red: "bg-red-100 text-red-800",
};

function Tag({ tag }: { tag: { label: string; type: string; color?: string } }) {
    if (tag.type === "duration" || tag.type === "speaker") {
        return <span className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium">{tag.label}</span>;
    }
    if (tag.type === "badge") {
        const colorClass = badgeColorMap[tag.color as keyof typeof badgeColorMap] || "bg-gray-100 text-gray-700";
        return <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${colorClass}`}>{tag.label}</span>;
    }
    return null;
}

function EventTimelineItem({ item, lastItem }: { item: EventTimelineItem; lastItem: boolean }) {
    const [hovered, setHovered] = useState(false);

    return (
        <div className="bg-white px-4 md:px-8 py-6 md:py-10">
            <div className="flex items-start cursor-default select-none" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
                <div className="w-20 flex-shrink-0 pt-0.5 pr-4 text-right text-xs text-gray-400 tracking-wide whitespace-nowrap">{item.time}</div>
                <div className="flex flex-col items-center  w-7 " style={{ height: "150px" }}>
                    <div
                        className={["w-3.5 h-3.5 rounded-full mt-0.5  transition-transform duration-200", hovered ? "transform scale-125" : "transform scale-100"].join(" ")}
                        style={{
                            border: "2px solid",
                            backgroundColor: item.dot === "filled" ? "rgba(30, 30, 30, 1)" : "white",
                            borderColor: item.dot === "filled" ? "rgba(30, 30, 30, 1)" : "rgba(233, 234, 236, 1)",
                        }}
                    />
                    {!lastItem && <div className="mt-2 bg-gray-300 flex-1" style={{ width: "2px", backgroundColor: "rgba(233, 234, 236, 1)" }} />}
                </div>

                <div className={["pl-5 transition-transform duration-200", hovered ? "transform translate-x-1" : "transform translate-x-0"].join(" ")}>
                    <p className="text-xs font-bold tracking-widest text-blue-600 uppercase mb-1">{item.category}</p>
                    <h2 className="text-xl font-bold text-gray-900 leading-snug mb-2">{item.title}</h2>
                    <p className="text-sm text-gray-500 leading-relaxed mb-3 max-w-xl">{item.description}</p>
                    <div className="flex flex-wrap gap-2">
                        {item.tags.map((tag, i) => (
                            <Tag key={i} tag={tag} />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

const EventTimeline: React.FC<EventTimelineProps> = ({ title, subtitle, items = [] }) => {
    const hasHeading = Boolean(title || subtitle);
    const hasItems = items.length > 0;

    if (!hasHeading && !hasItems) {
        return null;
    }

    return (
        <section className="lg:py-20 py-14" style={{ backgroundColor: "rgba(247, 250, 252, 1)", fontFamily: "Inter" }}>
            <div
                className={`${container_classname} rounded-lg p-10 py-20 bg-white`}
                style={{
                    boxShadow: "0px 4px 8px -2px rgba(16, 24, 40, 0.1)",
                }}
            >
                <div className="mx-auto max-w-5xl">
                    {hasHeading && (
                        <div className="">
                            {title && (
                                <h2 className="md:text-6xl text-3xl font-semibold text-gray-900 md:mb-4 mb-3" style={{ fontFamily: "Degular Display" }}>
                                    {title}
                                </h2>
                            )}
                            {subtitle && <p className="lg:text-md text-base text-gray-600">{subtitle}</p>}

                            <p className="mt-8 text-base font-medium font-inter mb-10" style={{ color: "rgba(26, 32, 44, 1)" }}>
                                The program is set. Part of what gets discussed will be shaped by who&apos;s in the room — by design.
                            </p>
                        </div>
                    )}
                    {hasItems && items.map((item: EventTimelineItem, index: number) => <EventTimelineItem key={item.title} item={item} lastItem={items.length - 1 === index} />)}
                </div>
            </div>
        </section>
    );
};

export default EventTimeline;
